import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import LabelIcon from '@atlaskit/icon/glyph/label';
import { Stack } from '@atlaskit/primitives';
import Tag from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import Tooltip from '@atlaskit/tooltip';

import GenericMessage from 'src/components/generic-message';
import { PullRequestLabel } from 'src/components/types';
import { getCurrentPullRequestUrlPieces } from 'src/redux/pull-request/selectors';

import { ConnectedExpander } from '../connected-expander';

import { AddLabel } from './add-label';
import {
  sendGetPullRequestLabelsRequest,
  sendRemovePullRequestLabelRequest,
} from './labels-utils';

export const PR_LABELS_CARD_COLLAPSED_LOCALSTORAGE_KEY =
  'pr.labels.card.collapsed';

export const LabelsCard = () => {
  const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);
  const [prLabels, setPrLabels] = useState<PullRequestLabel[]>([]);
  const [getLabelsIsError, setGetLabelsIsError] = useState(false);
  const [getLabelsIsLoading, setGetLabelsIsLoading] = useState(false);

  // const setPrLabelsInChild = (newPrLabels: PullRequestLabel[]) => {
  //   setPrLabels(newPrLabels);
  // };

  const onDelete = (labelContent: string) => {
    sendRemovePullRequestLabelRequest(owner, slug, id, labelContent).then(
      ({ isError }) => {
        if (!isError) {
          setPrLabels(
            prLabels.filter(item => item.label.content !== labelContent)
          );
        }
      }
    );
  };

  const onGet = useCallback(() => {
    setGetLabelsIsLoading(true);
    sendGetPullRequestLabelsRequest(owner, slug, id)
      .then(({ isError, labels }) => {
        setGetLabelsIsError(isError);
        if (!isError) {
          setPrLabels(labels);
        }
      })
      .catch(_ => {
        setGetLabelsIsError(true);
      })
      .finally(() => {
        setGetLabelsIsLoading(false);
      });
  }, [
    owner,
    slug,
    id,
    setPrLabels,
    setGetLabelsIsError,
    setGetLabelsIsLoading,
  ]);

  // Renders labels on first load.
  useEffect(() => {
    onGet();
  }, [onGet]);

  const icon = <LabelIcon label="" size="medium" />;

  // temporary replacement for i18n formatting:
  const UILabelsText =
    prLabels.length === 1
      ? `${prLabels.length} label`
      : `${prLabels.length} labels`;

  return (
    <ConnectedExpander
      localStorageKey={PR_LABELS_CARD_COLLAPSED_LOCALSTORAGE_KEY}
      icon={icon}
      label={
        <>
          <b>{prLabels.length}</b> {prLabels.length === 1 ? 'label' : 'labels'}
        </>
      }
      isLoading={getLabelsIsLoading}
      dataTestId="pr-labels-card"
      isPanelBodyOverflowVisible
      sectionAriaLabel="Labels"
      headingAriaLabel={UILabelsText}
      defaultIsCollapsed={false}
    >
      {getLabelsIsError && !getLabelsIsLoading ? (
        <GenericMessage iconType="warning" title="Couldn't load labels">
          <Button appearance="link" onClick={onGet}>
            Try again
          </Button>
        </GenericMessage>
      ) : (
        <Stack>
          <TagGroup>
            {prLabels &&
              prLabels.length > 0 &&
              prLabels.map(prLabel => {
                return (
                  <Tooltip
                    key={prLabel.label.content}
                    content={prLabel.label.content}
                  >
                    <Tag
                      text={prLabel.label.content}
                      isRemovable
                      onAfterRemoveAction={() =>
                        onDelete(prLabel.label.content)
                      }
                    />
                  </Tooltip>
                );
              })}
          </TagGroup>
          <AddLabel prLabels={prLabels} setPrLabels={setPrLabels} />
        </Stack>
      )}
    </ConnectedExpander>
  );
};
